import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import { useNavigation } from '@react-navigation/native';
import {
  IconLink,
  ScrollViewWithHeaderTitle,
  theme,
} from '@sweetgreen/garnish';

import {
  AccountScreenTitle,
  CreditOverviewPromoCodeForm,
} from '@order/components';
import { usePromoCode } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { useHeaderRight } from '../../../components/Header/Header.hooks';
import { TransparentModal } from '../../../components/TransparentModal';
import { ScannerComponent } from '../ScanToPay.components';
import { useScanToPayNavigation } from '../ScanToPay.navigation';

export const ScanToPayGiftCode = () => {
  const { t } = useLocalizationContext();
  const title = t('account.scan-to-pay.promo-gift-code');
  const {
    promoCode,
    setPromoCode,
    submitPromoCode,
    promoCodeInvalid,
    isPromoCodeApplied,
    isSubmittingPromoCode,
  } = usePromoCode();
  const { goBack } = useScanToPayNavigation();
  const { bottom, top } = useSafeAreaInsets();
  const dynamicPadding = useStyle(() => {
    return {
      paddingTop: Math.max(theme.spacing['4'], top),
      paddingBottom: Math.max(theme.spacing['4'], bottom),
    };
  }, [top, bottom]);
  const [isScanning, setIsScanning] = useState(false);

  // ─────────────────────── Telemetry ──────────────────────────────────

  const { track } = useTelemetry();

  useTrackEffect('payment_credit.view_promo_code');

  const onMonitoredCameraShown = useCallback(() => {
    track('payment_credit.camera_opened');
  }, [track]);

  // ─────────────────────── Callbacks ──────────────────────────────────

  const handleShowScanner = useCallback(() => {
    onMonitoredCameraShown();
    setIsScanning(true);
  }, [onMonitoredCameraShown]);

  const handleHideScanner = useCallback(() => {
    setIsScanning(false);
  }, []);

  const handleGoBack = useCallback(() => {
    setPromoCode('');
    goBack();
  }, [goBack, setPromoCode]);

  const onPromoCodeScanned = useCallback(
    (code: string) => {
      setPromoCode(code);
      setIsScanning(false);
    },
    [setIsScanning, setPromoCode],
  );

  // ─────────────────────── Navigation ─────────────────────────────────

  const navigation = useNavigation();

  const navigationHeaderRightItems = useMemo<NavigationHeaderRightItems>(
    () => [
      {
        key: 'account.gift-code.header.close-btn',
        icon: 'IconClose',
        accessibilityLabel: t('general.close'),
        onPress: handleGoBack,
      },
    ],
    [handleGoBack, t],
  );
  const navigationHeaderRight = useHeaderRight({
    items: navigationHeaderRightItems,
  });

  // removes the header when scanning.
  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: !isScanning,
      headerRight: navigationHeaderRight,
    });
  }, [isScanning, navigation, navigationHeaderRight]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal size="full" onClose={handleGoBack} disableAnimation>
      {isScanning ? (
        <>
          <FloatingCloseScanIcon onClose={handleHideScanner} />
          <ScannerComponent
            onPromoCodeScanned={onPromoCodeScanned}
            handleHideScanner={handleHideScanner}
          />
        </>
      ) : (
        <ScrollViewWithHeaderTitle testID="account.gift-code.root.container">
          <View
            testID="scan-to-pay.gift-code.route"
            style={[styles.padding, dynamicPadding]}
          >
            <AccountScreenTitle title={title} />
            <CreditOverviewPromoCodeForm
              buttonPlacement="bottom"
              onPressApplyPromoCode={submitPromoCode}
              hasPromoCodeError={promoCodeInvalid}
              promoCode={promoCode}
              isPromoCodeAlreadyApplied={isPromoCodeApplied}
              setPromoCode={setPromoCode}
              handleShowScanner={handleShowScanner}
              isSubmittingPromoCode={isSubmittingPromoCode}
            />
          </View>
        </ScrollViewWithHeaderTitle>
      )}
    </TransparentModal>
  );
};

export const FloatingCloseScanIcon = ({
  onClose,
}: {
  readonly onClose: () => void;
}) => {
  const { t } = useLocalizationContext();

  return (
    <IconLink
      width={24}
      height={24}
      name="IconClose"
      testID="qr-code.scanner.close"
      color={theme.colors.GREEN_1}
      accessibilityLabel={t('general.close')}
      onPress={onClose}
      style={styles.floatingButton}
    />
  );
};

const styles = StyleSheet.create({
  padding: {
    flex: 1,
    backgroundColor: theme.colors.KHAKI_3,
    paddingHorizontal: theme.spacing['4'],
  },
  floatingButton: {
    position: 'absolute',
    padding: theme.spacing['1'],
    top: theme.spacing['4'],
    right: theme.spacing['4'],
    zIndex: theme.zIndex.popover,
    backgroundColor: theme.colors.OPACITY.KHAKI_3.DARKEST,
    borderRadius: 1000,
    ...theme.elevations['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type NavigationHeaderRightItems = Parameters<typeof useHeaderRight>[0]['items'];
